import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface EmissionPoint {
  id: string;
  establishment_code: string;
  pos_code: string;
  label: string;
  is_default: boolean;
}

interface Pref {
  emission_point_id: string;
}

interface Props {
  value: string | null;
  onChange: (id: string) => void;
}

export function EmissionPointPicker({ value, onChange }: Props) {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data: list } = useQuery({
    queryKey: ['emission-points', activeOrgId],
    queryFn: () => api<EmissionPoint[]>('/bookkeeping/emission-points'),
    enabled: !!activeOrgId,
  });

  const { data: pref } = useQuery({
    queryKey: ['emission-point-preference', activeOrgId],
    queryFn: () => api<Pref | null>('/bookkeeping/me/emission-point-preference'),
    enabled: !!activeOrgId,
  });

  useEffect(() => {
    if (value || !list || list.length === 0) return;
    const initial =
      (pref && list.find((ep) => ep.id === pref.emission_point_id)?.id) ??
      list.find((ep) => ep.is_default)?.id ??
      list[0]?.id;
    if (initial) onChange(initial);
  }, [value, list, pref, onChange]);

  if (!list || list.length <= 1) return null;

  return (
    <label className="block text-sm">
      {t('emissionPoint.title')}
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded border px-2 py-1"
      >
        {list.map((ep) => (
          <option key={ep.id} value={ep.id}>
            {ep.label} ({ep.establishment_code}-{ep.pos_code})
          </option>
        ))}
      </select>
    </label>
  );
}
